import { jwtDecode } from 'jwt-decode';

// Define a version for easier debugging
const VERSION = '1.0.3';
const TOKEN_KEY = 'apiAuthToken';
const SCOPE = 'TokenAuth';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second delay between retries

/**
 * Helper function to add delay between retries
 * @param ms Milliseconds to wait
 */
const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Validates a JWT token's basic format
 * @param token The token to validate
 * @returns True if the token is a valid JWT, false otherwise
 */
const isValidTokenFormat = (token: string | null): boolean => {
    if (!token) return false;

    // Check for basic JWT format (three parts separated by dots)
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
};

/**
 * Gets a valid JWT token, handling token expiry and undefined tokens
 * @param auth The authentication object
 * @returns A valid token or null if unavailable
 */
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

export function getOrCreateSessionId() {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
        // Use crypto.randomUUID if available, otherwise fallback to a simple UUID generator
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            sessionId = window.crypto.randomUUID();
        } else {
            // Fallback: simple UUID v4 generator (string-based, no arithmetic on arrays)
            sessionId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
        localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
}

/**
 * Gets a valid token for the current user/session.
 * @param auth The authentication object
 * @param options Optional: { rawIdTokenOnly: boolean } - if true, returns only the raw JWT (for APIs that require it)
 * @returns A valid token or null if unavailable
 */
export async function getValidToken(auth: any, options?: { rawIdTokenOnly?: boolean }): Promise<string | null> {
    // Always fetch the shared session token from Firestore for this sessionId
    try {
        const sessionId = getOrCreateSessionId();
        const user = auth?.currentUser;
        if (!user) {
            console.warn(`[${SCOPE}@${VERSION}] No current user for Firestore session token fetch.`);
            return null;
        }
        const uid = user.uid;
        const sessionRef = firebase.firestore().collection('users').doc(uid).collection('sessions').doc(sessionId);
        const doc = await sessionRef.get();
        let token: string | null = null;
        if (doc.exists && doc.data()?.token) {
            token = doc.data()?.token;
            // Validate token format
            if (!isValidTokenFormat(token.split('--__--')[0])) {
                console.warn(`[${SCOPE}@${VERSION}] Invalid token format in Firestore for session ${sessionId}`);
                token = null;
            }
        } else {
            console.warn(`[${SCOPE}@${VERSION}] No session token found in Firestore for session ${sessionId}`);
        }

        // Fallback: try to getIdToken if Firestore token missing/invalid
        if (!token) {
            try {
                token = await user.getIdToken();
            } catch (e) {
                console.error(`[${SCOPE}@${VERSION}] Error getting fallback token from Firebase Auth:`, e);
                return null;
            }
        }

        // Store in sessionStorage if valid
        if (token && isValidTokenFormat(token.split('--__--')[0])) {
            sessionStorage.setItem(TOKEN_KEY, token);
            // If the caller wants only the raw JWT, return just that part
            if (options?.rawIdTokenOnly) {
                return token.split('--__--')[0];
            }
            return token;
        }
        return null;
    } catch (e) {
        console.error(`[${SCOPE}@${VERSION}] Error fetching session token from Firestore:`, e);
        return null;
    }
}